import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import ApiClient from '../../utils/api/ApiClient'
import { Organization, OrganizationSetting } from '../../types'
import { STATUS } from '../../constants'
import { selectClinic } from '../NewScriptPage/ScriptForm/ClinicContextSlice'
import { OrganizationUsersClient, setOrganizationUsers } from '../../features/users/UsersSlice'
interface OrganizationState {
  data?: Partial<Organization>
  status: {
    fetchById: STATUS
    updateOrganizationProfile: STATUS
    updateOrganizationSetting: STATUS
  }
}

export const fetchOrganizationData = createAsyncThunk(
  'organization/fetchById',
  async (_, { dispatch }) => {
    const response = await ApiClient.get<Organization>(`bff/organizations/current`)
    const organization = response.data
    if (organization) {
      const clinic = organization.clinic
      clinic && dispatch(selectClinic(clinic))
      const users = await OrganizationUsersClient.getAllUsersInOrg()
      users && dispatch(setOrganizationUsers(users))
      return organization
    }
  }
)

export const updateOrganizationData = createAsyncThunk(
  'organization/updateOrganizationProfile',
  async (newOrganization: Partial<Organization>) => {
    const { id, ...data } = newOrganization
    const result = await ApiClient.patch(`organization/${id}`, data)
    return result.data
  }
)

export const updateOrganizationSettingsData = createAsyncThunk(
  'organization/updateOrganizationSettingsProfile',
  async (newOrganizationSettings: Partial<OrganizationSetting>) => {
    const result = await ApiClient.patch(
      `bff/organizations/current/settings`,
      newOrganizationSettings
    )
    return result.data
  }
)

export const createOrganizationByAdmin = async (newOrganizationData: any) => {
  const { data } = await ApiClient.post('organization', newOrganizationData)
  return data
}

type createOrganizationProps = {
  clinicId: string
}

export const createOrganization = async (
  newOrganizationData: createOrganizationProps
): Promise<Organization> => {
  const { data } = await ApiClient.post('bff/organizations', newOrganizationData)
  return data
}

export const createOrganizationByHPIO = async (newOrganizationData: {
  name: string
  hpio: string
}) => {
  const { data } = await ApiClient.post('bff/organizations/create-by-hpio', newOrganizationData)
  return data
}

export const getOrganizationsByHPIO = async (hpio: string): Promise<Organization[]> => {
  const { data } = await ApiClient.get(`bff/organizations/search?hpio=${hpio}`)
  return data
}

const initialState: OrganizationState = {
  status: {
    fetchById: STATUS.DEFAULT,
    updateOrganizationProfile: STATUS.DEFAULT,
    updateOrganizationSetting: STATUS.DEFAULT,
  },
}

const OrganizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationData.fulfilled, (state, { payload }) => {
        state.data = payload
        state.status = {
          ...state?.status,
          fetchById: STATUS.SUCCESS,
        }
      })
      .addCase(fetchOrganizationData.rejected, (state) => {
        state.status = {
          ...state?.status,
          fetchById: STATUS.FAILURE,
        }
      })
      .addCase(fetchOrganizationData.pending, (state) => {
        state.status = {
          ...state?.status,
          fetchById: STATUS.PENDING,
        }
      })
      .addCase(updateOrganizationData.fulfilled, (state, { payload }) => {
        state.data = payload
        state.status = {
          ...state?.status,
          updateOrganizationProfile: STATUS.SUCCESS,
        }
      })
      .addCase(updateOrganizationData.pending, (state) => {
        state.status = {
          ...state?.status,
          updateOrganizationProfile: STATUS.PENDING,
        }
      })
      .addCase(updateOrganizationSettingsData.fulfilled, (state, { payload }) => {
        state.status = {
          ...state?.status,
          updateOrganizationSetting: STATUS.SUCCESS,
        }

        state.data = {
          ...state.data,
          settings: payload,
        }
      })
      .addCase(updateOrganizationSettingsData.pending, (state) => {
        state.status = {
          ...state?.status,
          updateOrganizationSetting: STATUS.PENDING,
        }
      })
      .addCase(updateOrganizationSettingsData.rejected, (state) => {
        state.status = {
          ...state?.status,
          updateOrganizationSetting: STATUS.FAILURE,
        }
      })
  },
})

export default OrganizationSlice.reducer
